import { Vec3 } from '../vector';
import type {
  CameraIntrinsics,
  ColmapCameraData,
  ColmapData,
  ColmapDataset,
  ColmapSparseText,
  Point2D,
  Point3D,
} from './types';
import { dataLines, readInteger, readNumber, readString } from './utils';

type RawImage = ColmapCameraData & { camera_id?: number; img_name?: string; points?: Point2D[] };
type ParsedImage = ColmapCameraData & { points: Point2D[] };

/**
 * COLMAP 结构化数据只保留原生实体；运镜位姿等消费侧结构在 trackFromColmap 阶段临时派生。
 */
export class Colmap {
  private constructor(
    readonly cameras: CameraIntrinsics[],
    readonly images: ColmapCameraData[],
    readonly points3D: Point3D[],
    readonly datasets: ColmapDataset[],
  ) {}

  get pointCenter(): Vec3 | undefined {
    return averagePoints(
      this.points3D.map((point) => new Vec3(point.position.x, point.position.y, point.position.z)),
    );
  }

  static fromJson(data: ColmapData): Colmap {
    const cameras = (data.cameras ?? []).map(normalizeCamera).filter(isDefined);
    const parsedImages = (data.images ?? [])
      .map((image, index) => normalizeImage(image, index))
      .filter(isDefined);
    const images = parsedImages.map(stripImagePoints);
    const points3D: Point3D[] = [];
    return new Colmap(cameras, images, points3D, buildDatasets(cameras, parsedImages, points3D));
  }

  static fromSparseText(sources: ColmapSparseText): Colmap {
    const cameras = sources.camerasText ? parseCamerasText(sources.camerasText) : [];
    const parsedImages = sources.imagesText ? parseImagesText(sources.imagesText) : [];
    const images = parsedImages.map(stripImagePoints);
    const points3D = sources.points3DText ? parsePoints3DText(sources.points3DText) : [];
    return new Colmap(cameras, images, points3D, buildDatasets(cameras, parsedImages, points3D));
  }
}

function normalizeCamera(camera: CameraIntrinsics): CameraIntrinsics | null {
  const id = readInteger(camera.id);
  const width = readInteger(camera.width);
  const height = readInteger(camera.height);
  const model = readString(camera.model);
  const params = Array.isArray(camera.params)
    ? camera.params.map(Number).filter(Number.isFinite)
    : [];
  if (
    id === undefined ||
    width === undefined ||
    height === undefined ||
    !model ||
    params.length === 0
  )
    return null;
  return { id, model, width, height, params };
}

function normalizeImage(image: RawImage, fallbackId: number): ParsedImage | null {
  const id = readInteger(image.id) ?? fallbackId;
  const cameraId = readInteger(image.cameraId ?? image.camera_id);
  const name = readString(image.name) ?? readString(image.img_name) ?? `${id}`;
  const poseValues = [image.qw, image.qx, image.qy, image.qz, image.tx, image.ty, image.tz].map(
    Number,
  );
  if (cameraId === undefined || poseValues.some((value) => !Number.isFinite(value))) return null;
  return {
    id,
    qw: poseValues[0],
    qx: poseValues[1],
    qy: poseValues[2],
    qz: poseValues[3],
    tx: poseValues[4],
    ty: poseValues[5],
    tz: poseValues[6],
    cameraId,
    name,
    points: Array.isArray(image.points) ? image.points.map(normalizePoint2D).filter(isDefined) : [],
  };
}

function parseCamerasText(text: string): CameraIntrinsics[] {
  const result: CameraIntrinsics[] = [];
  for (const line of dataLines(text)) {
    const parts = line.split(/\s+/);
    if (parts.length < 5) continue;
    const camera = normalizeCamera({
      id: Number(parts[0]),
      model: parts[1],
      width: Number(parts[2]),
      height: Number(parts[3]),
      params: parts.slice(4).map(Number),
    });
    if (camera) result.push(camera);
  }
  return result;
}

function parseImagesText(text: string): ParsedImage[] {
  const result: ParsedImage[] = [];
  const lines = dataLines(text);

  for (let i = 0; i < lines.length; i += 2) {
    const parts = lines[i].split(/\s+/);
    if (parts.length < 10) continue;
    const image = normalizeImage(
      {
        id: Number(parts[0]),
        qw: Number(parts[1]),
        qx: Number(parts[2]),
        qy: Number(parts[3]),
        qz: Number(parts[4]),
        tx: Number(parts[5]),
        ty: Number(parts[6]),
        tz: Number(parts[7]),
        cameraId: Number(parts[8]),
        name: parts.slice(9).join(' '),
        points: parsePoints2DLine(lines[i + 1] ?? ''),
      },
      result.length,
    );
    if (image) result.push(image);
  }

  return result;
}

function parsePoints2DLine(line: string): Point2D[] {
  const parts = line.split(/\s+/);
  const result: Point2D[] = [];
  for (let i = 0; i + 2 < parts.length; i += 3) {
    const point = normalizePoint2D({
      x: Number(parts[i]),
      y: Number(parts[i + 1]),
      point3dId: Number(parts[i + 2]),
    });
    if (point) result.push(point);
  }
  return result;
}

function normalizePoint2D(point: Point2D): Point2D | null {
  const x = readNumber(point.x);
  const y = readNumber(point.y);
  const point3dId = readInteger(point.point3dId);
  if (x === undefined || y === undefined || point3dId === undefined) return null;
  return { x, y, point3dId };
}

function stripImagePoints(image: ParsedImage): ColmapCameraData {
  const { points: _points, ...cameraData } = image;
  return cameraData;
}

function parsePoints3DText(text: string): Point3D[] {
  const result: Point3D[] = [];
  for (const line of dataLines(text)) {
    const parts = line.split(/\s+/);
    if (parts.length < 8) continue;
    const point = normalizePoint3D({
      id: Number(parts[0]),
      position: { x: Number(parts[1]), y: Number(parts[2]), z: Number(parts[3]) },
      color: { r: Number(parts[4]), g: Number(parts[5]), b: Number(parts[6]) },
      error: Number(parts[7]),
      observations: parsePoint3DObservations(parts.slice(8)),
    });
    if (point) result.push(point);
  }
  return result;
}

function normalizePoint3D(point: Point3D): Point3D | null {
  const id = readInteger(point.id);
  const x = readNumber(point.position.x);
  const y = readNumber(point.position.y);
  const z = readNumber(point.position.z);
  const r = readInteger(point.color.r);
  const g = readInteger(point.color.g);
  const b = readInteger(point.color.b);
  const error = readNumber(point.error);
  if (
    id === undefined ||
    x === undefined ||
    y === undefined ||
    z === undefined ||
    r === undefined ||
    g === undefined ||
    b === undefined ||
    error === undefined
  ) {
    return null;
  }
  return {
    id,
    position: { x, y, z },
    color: { r, g, b },
    error,
    observations: point.observations,
  };
}

function parsePoint3DObservations(parts: string[]): Point3D['observations'] {
  const result: Point3D['observations'] = [];
  for (let i = 0; i + 1 < parts.length; i += 2) {
    const imageId = readInteger(parts[i]);
    const point2dIdx = readInteger(parts[i + 1]);
    if (imageId !== undefined && point2dIdx !== undefined) result.push({ imageId, point2dIdx });
  }
  return result;
}

function buildDatasets(
  cameras: CameraIntrinsics[],
  images: ParsedImage[],
  points3D: Point3D[],
): ColmapDataset[] {
  const cameraMap = new Map(cameras.map((camera) => [camera.id, camera]));
  const pointMap = new Map(points3D.map((point) => [point.id, point]));
  return images.flatMap((image) => {
    const intrinsics = cameraMap.get(image.cameraId);
    if (!intrinsics) return [];
    return [
      {
        id: image.id,
        quaternion: { w: image.qw, x: image.qx, y: image.qy, z: image.qz },
        position: { x: image.tx, y: image.ty, z: image.tz },
        cameraId: image.cameraId,
        name: image.name,
        points: image.points,
        intrinsics,
        observedPoints3D: image.points.flatMap((point) => pointMap.get(point.point3dId) ?? []),
      },
    ];
  });
}

function averagePoints(points: Vec3[]): Vec3 | undefined {
  if (points.length === 0) return undefined;
  const sum = points.reduce((acc, point) => acc.add(point), Vec3.ZERO);
  return sum.scale(1 / points.length);
}

function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}
